// eslint-disable-next-line no-restricted-imports
import { ToastId, useToast as useChakraToast } from "@chakra-ui/toast";
import log from "loglevel";
import LogRocket from "logrocket";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { serializeError } from "serialize-error";
import { Toast, ToastVariant } from "shared/components/toast/Toast";
import { AppConfig } from "shared/config/AppConfig";
import { getErrorMessage } from "shared/utils/ErrorUtils";

export interface UseToastProps {
  id?: string;
  message: React.ReactNode;
  variant: ToastVariant;
  isClosable?: boolean;
  action?: {
    title: string;
    onClick: () => void;
  };
}

interface UseToastReturn {
  showToast: (props: UseToastProps) => ToastId;
  showErrorToast: (error: unknown) => ToastId;
  closeToast: (id: ToastId) => void;
  toastIsActive: (id: ToastId) => boolean;
}

// FIXME: Not possible to use our own <Toast> by default due to this issue:
//  https://github.com/chakra-ui/chakra-ui/issues/6370
//  This custom hook might not be needed once that's fixed.
export const useToast = (): UseToastReturn => {
  const chakraToast = useChakraToast();
  const { t } = useTranslation(["common"]);
  return useMemo<UseToastReturn>(() => {
    const showToast = ({
      id,
      isClosable = true,
      ...props
    }: UseToastProps): ToastId =>
      chakraToast({
        id,
        position: "bottom-right",
        render: ({ onClose }) => (
          <Toast
            isLoading={!isClosable}
            onClose={isClosable ? onClose : undefined}
            {...props}
          />
        ),
        duration: isClosable ? 4_000 : null,
      });
    const showErrorToast = (error: unknown): ToastId => {
      const message = getErrorMessage(t, error);
      log.error(message, serializeError(error));
      if (AppConfig.logRocket) {
        LogRocket.track("error-toast", { message });
      }
      return showToast({ variant: "error", message });
    };
    return {
      showToast,
      showErrorToast,
      closeToast: chakraToast.close.bind(chakraToast),
      toastIsActive: chakraToast.isActive.bind(chakraToast),
    };
  }, [chakraToast, t]);
};
